/**
 * Recording at a rate the student's connection can actually carry.
 *
 * The bitrates were fixed: 300 kbps for the webcam, 500 kbps for the screen,
 * on top of the phone's own 600 kbps. On a good line that is nothing. On a
 * slow uplink it is a system that produces bytes several times faster than it
 * can send them, so the backlog grows for the entire exam and can never
 * recover — the queue only ever gets longer, and whatever is still in it at
 * the end is at risk.
 *
 * Warning the student, which the app already did, does not help: there is
 * nothing they can do about their own uplink mid-exam. The only real lever is
 * to make less data.
 *
 * The rules are deliberately conservative, because changing the bitrate means
 * restarting the MediaRecorder, and a restart begins a new session — a new
 * WebM header, a new segment for the player to stitch. That is safe now that
 * chunks carry their session, but it is not free, so it must happen rarely and
 * only on strong evidence.
 *
 *  - **Only on a sustained backlog.** A momentary queue is normal: chunks are
 *    written every ten seconds and uploaded in bursts. Only a backlog that
 *    stays large for a full minute counts.
 *  - **Only downwards.** Stepping back up when the queue drains would
 *    oscillate — each change costing another restart — and the drain is
 *    exactly what a lower bitrate is supposed to produce, so recovery is not
 *    evidence that the connection improved.
 *  - **A floor.** Below the last rung the picture stops being usable as
 *    proctoring evidence, and unusable footage that uploads is no better than
 *    good footage that does not.
 */

/**
 * Ladders per source, highest first — ordered by what the footage is FOR.
 *
 * The first rung of each is the rate that source records at normally, so this
 * is the single source of truth for the starting bitrates too: the setup and
 * exam pages read rung 0 from here rather than repeating a number that could
 * drift away from the ladder beneath it.
 *
 * These are not equal. When the connection cannot carry everything, the
 * question is not "how do we shrink all three" but "which one would you still
 * want to be able to read afterwards".
 *
 *  - **Screen is the evidence.** It is the only recording showing what the
 *    student actually did — which question was open, what they typed, what
 *    else was on the display. Nothing else stands in for it, and text stops
 *    being legible long before a face stops being recognisable. It starts
 *    highest and its ladder is the shallowest: it gives up the least.
 *  - **The laptop webcam** only has to answer "is this the same person, and
 *    are they looking away". That survives a low bitrate, so it drops further.
 *  - **The phone** is the most redundant: a side view of a desk the other two
 *    already cover, and it is already the lowest of the three at 250 kbps
 *    because a mid-range Android drops frames above that. It keeps that
 *    ceiling and takes the deepest floor.
 *
 * The phone uploads over its OWN connection, so dropping it does not relieve
 * the laptop. That is fine: it is dropped for its own sake, not to buy the
 * screen room.
 */
export const BITRATE_LADDER = {
  screen: [500_000, 380_000, 260_000],
  webcam: [300_000, 150_000, 90_000],
  phone: [250_000, 160_000, 100_000],
} as const satisfies Record<string, readonly number[]>;

export type AdaptiveSource = keyof typeof BITRATE_LADDER;

/**
 * Queued segments that count as a real backlog, per source.
 *
 * Different per source ON PURPOSE — this is what actually enforces the
 * priority above. The webcam and the screen share one uplink, so when the
 * laptop cannot keep up they build a backlog at the same moment; with a single
 * shared trigger they would step down together, spending the screen's quality
 * to fix a problem the webcam could have fixed on its own.
 *
 * So the webcam trips first, on a shorter queue. Only if the backlog survives
 * that does the screen give anything up. The phone sits between: it is on its
 * own connection, so its trigger reflects the phone's link rather than the
 * laptop's, and there is no reason to protect it.
 *
 * Chunks are ten seconds each, so four is forty seconds of video waiting.
 */
export const BACKLOG_CHUNKS = {
  webcam: 4,
  phone: 5,
  screen: 8,
} as const satisfies Record<AdaptiveSource, number>;

/** How long the backlog must persist before it is believed. */
export const SUSTAINED_MS = 60_000;

export interface BacklogState {
  /** When the current run of backlog began, or null if there is none. */
  since: number | null;
  /** Rungs already taken. */
  step: number;
}

export const NO_BACKLOG: BacklogState = { since: null, step: 0 };

export interface BacklogInput {
  pending: number;
  nowMs: number;
  state: BacklogState;
  source: AdaptiveSource;
}

export interface BacklogDecision {
  state: BacklogState;
  /** The bitrate to record at from now on. */
  bitrate: number;
  /** True on the tick that decided to step down — for logging and the UI. */
  steppedDown: boolean;
}

/**
 * Folds one status reading into the backlog state and returns the bitrate to
 * use. Pure, so the policy can be tested without a MediaRecorder.
 */
export function nextBitrate({
  pending,
  nowMs,
  state,
  source,
}: BacklogInput): BacklogDecision {
  const ladder = BITRATE_LADDER[source];
  const current = ladder[Math.min(state.step, ladder.length - 1)]!;

  if (pending < BACKLOG_CHUNKS[source]) {
    // The run is broken. The step already taken is KEPT: a queue that drained
    // is the lower bitrate working, not the connection recovering, and
    // restoring the old rate here would put us straight back into backlog and
    // cost a restart each way.
    return { state: { since: null, step: state.step }, bitrate: current, steppedDown: false };
  }

  const since = state.since ?? nowMs;
  const atFloor = state.step >= ladder.length - 1;

  if (!atFloor && nowMs - since >= SUSTAINED_MS) {
    const step = state.step + 1;
    return {
      // The clock restarts, so the next step needs its own full minute of
      // evidence rather than arriving immediately after this one.
      state: { since: null, step },
      bitrate: ladder[step]!,
      steppedDown: true,
    };
  }

  return { state: { since, step: state.step }, bitrate: current, steppedDown: false };
}
